import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpResponse } from "@angular/common/http";
import { Log } from "ng2-logger";
import { ToastsManager } from "@app/shared/util/toastr.service";

import * as FileSaver from "file-saver";
import { ConfirmationModalComponent } from "@components/index";
import { PermissionService } from "@app/authentication/services/permission.service";
import { TableConfig, TableConfigUtils } from '@app/shared/util/table-config.utils';
import { Ticket } from '@models/ticket.model';
import { TicketCreateModalComponent } from './components/ticket-create-modal/ticket-create-modal.component';
import { TicketDetailsModalComponent } from "./components/ticket-details-modal/ticket-details-modal.component";
import { TicketService } from '@services/ticket.service';
import { TranslateParser, TranslateService } from '@ngx-translate/core';
import { TreeAdapter } from '@app/shared/util/tree-adapter.utils';
import { TreeModel } from '@app/shared/components/tree/tree.model';
import { TicketType } from "@models/enums/ticket-type.enum";
import { Router } from '@angular/router';
import { NavigationService } from '@app/core/services/navigation.service';

@Component({
    selector: 'app-tickets',
    templateUrl: './tickets.component.html',
    styleUrls: ['./tickets.component.scss']
})
export class TicketManagementComponent implements OnInit {
    _logger = Log.create(TicketManagementComponent.name);

    @ViewChild(TicketCreateModalComponent, {static: false}) ticketFormModal: TicketCreateModalComponent;
    @ViewChild(TicketDetailsModalComponent, {static: true}) ticketDetailsModal: TicketDetailsModalComponent;
    @ViewChild('errorModal', {static: true}) errorModal: ConfirmationModalComponent;

    confirmationPopupMessage: string;

    public tickets: Ticket[] = [];
    public tableConfig = new TableConfig();
    public isLoadingTickets = false;
    public isLoading = false;
    public similarTickets: TreeModel[];

    selectedTicket: Ticket;
    canManageTickets = false;

    constructor(private ticketService: TicketService,
                private toastr: ToastsManager,
                private treeAdapter: TreeAdapter,
                private permissionService: PermissionService,
                private translationService: TranslateService,
                private translateParser: TranslateParser,
                private router: Router,
                private navigationService: NavigationService) {
    }

    ngOnInit() {
        this.getTickets(this.tableConfig);
        this.renderForManageTickets();
    }


    onFilter(tableConfig: TableConfig) {
        this.getTickets(tableConfig);
    }

    pageChange(tableConfig: TableConfig) {
        this.getTickets(tableConfig);
    }

    openTicketModal() {
        this.ticketFormModal.open(null, TicketType.Normal);
    }

    getTickets(tableConfig = this.tableConfig) {
        this.isLoadingTickets = true;
        this.ticketService.getAllTickets(tableConfig).subscribe(
            (data: any) => {
                this._logger.info("getTickets:: API Returned data", data);
                this.tickets = data.tickets;
                this.tickets = TableConfigUtils.addColorToTicketByPriority(this.tickets);
                this.tableConfig.pager.count = data.count;
                this.isLoadingTickets = false;
            }, (error: any) => {
                this.isLoadingTickets = false;
                this._logger.error("getTickets:: API Returned error", error);
            });
    }


    createTicket(ticket) {
        this.isLoading = true;
        this.ticketService.createTicket(ticket).subscribe(
            (data: any) => {
                this._logger.info("createTicket:: API Returned data", data);
                this.getTickets();
                this.toastr.success(this.buildCreateMessage(data));
                this.isLoading = false;
                this.ticketFormModal.close();
            },
            (error: any) => {
                this.isLoading = false;
                this.ticketFormModal.close();
                if (error && error.error) {
                    this.confirmationPopupMessage = error.error.message;
                    this.errorModal.open();
                }
                this._logger.error("createTicket:: API Returned error", error);

            }
        );
    }

    checkForSimilarTickets(ticket) {
        this.isLoading = true;
        this.ticketService.checkForSimilarTickets(ticket).subscribe(
            (data: any) => {
                this._logger.info("checkForSimilarTickets:: API Returned data", data)
                this.similarTickets = this.treeAdapter.adaptTicketOverviewToTree(data, 'ticketId');
                this._logger.info("checkForSimilarTickets:: Tree data", this.similarTickets);
                this.isLoading = false;
            },
            (error: any) => {
                this.isLoading = false;
                this._logger.error("createTicket:: API Returned error", error);
            });
    }

    onDownloadFile(fileToDownload: any) {
        this.ticketService.downloadFile(fileToDownload)
    }

    buildCreateMessage(ticketId) {
        const message = this.translationService.instant('admin.tickets.toasts.created');
        return this.translateParser.interpolate(message, {ticketId: ticketId});
    }

    onSortChange(tableState) {
        this.getTickets(tableState);
    }

    onTableRowClick(ticket: Ticket) {
        this.router.navigateByUrl(`/ticket-management/${ticket.id}`);
        this.navigationService.addToHistory(this.router.url);
    }

    shouldRenderManageOperationsButton() {
        return this.permissionService.hasHasPermissionForFeature('VIEW_OPERATIONS');
    }

    onDownloadTicketsReport(tableConfig = this.tableConfig) {
        this.ticketService.downloadTicketsReport(tableConfig).subscribe(
            (data: HttpResponse<any>) => {
                if (data.body != null) {
                    this._logger.info('onDownloadTicketsReport:: API Returned error', data);
                    const blob = new Blob([data.body], {type: 'application/xsls'});
                    const filename = data.headers.get('x-filename');
                    FileSaver.saveAs(blob, filename);
                }
            },
            (error: any) => {
                this._logger.error('onDownloadTicketsReport:: API Returned error', error);
            }
        );
    }

    renderForManageTickets() {
        this.canManageTickets = this.permissionService.hasHasPermissionForFeature('MANAGE_TICKETS');
    }

    onFilterChange(reportFilters: any[]) {
        this.tableConfig.removeFilter('createdDateStart');
        this.tableConfig.removeFilter('createdDateEnd');
        reportFilters.forEach(element => {
            if (element.field === 'startDate') {
                this.tableConfig.upsertFilter('createdDateStart', element.value);
            } else {
                if (element.field === 'endDate') {
                    this.tableConfig.upsertFilter('createdDateEnd', element.value);
                } else {
                    this.tableConfig.upsertFilter(element.field, element.value);
                }
            }
        });
        this.getTickets();
    }
}
